import {
  VuiButtonSecondary,
  VuiCheckbox,
  VuiFlexContainer,
  VuiFlexItem,
  VuiLink,
  VuiSpacer,
  VuiSpinner,
  VuiTable,
  VuiText,
  VuiTextInput
} from "@vectara/vectara-ui";
import { useState, useEffect } from "react";
import { useLoginContext } from "../utils/LoginContext";
import { searchCustomers } from "../api/api";
import { Customer, CustomerStatus } from "../utils/commonTypes";
import { useCustomerContext } from "../utils/CustomerContext";

export const SearchCustomer = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [searchedCustomers, setSearchedCustomers] = useState<Array<Customer>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  // Status filter checkboxes - default to only ACT customers
  const [statusFilters, setStatusFilters] = useState({
    [CustomerStatus.ACT]: true,
    [CustomerStatus.EXP]: false,
    [CustomerStatus.MDEL]: false,
    [CustomerStatus.DEL]: false
  });

  const { updateCustomer } = useCustomerContext();
  const { authToken, isAuthenticated } = useLoginContext();

  // Automatically search when status filters change (but only if there's a search term)
  useEffect(() => {
    if (searchTerm && searchTerm.length > 0) {
      doSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilters]);

  const doSearch = async () => {
    if (isLoading) return;
    if (!searchTerm) {
      setSearchedCustomers([]);
      return;
    }
    
    // Get selected statuses from checkboxes
    const selectedStatuses = Object.entries(statusFilters)
      .filter(([_, isSelected]) => isSelected)
      .map(([status, _]) => status as CustomerStatus);
    
    if (selectedStatuses.length === 0) {
      setSearchedCustomers([]);
      return;
    }
    
    setIsLoading(true);
    try {
      if (authToken && isAuthenticated) {
        const customers = await searchCustomers(authToken, searchTerm, selectedStatuses);
        setSearchedCustomers(customers);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const seedCustomerContext = (customer: Customer) => () => {
    updateCustomer(customer);
  };

  const handleStatusFilterChange = (status: CustomerStatus) => {
    setStatusFilters(prev => ({
      ...prev,
      [status]: !prev[status]
    }));
  };

  const getStatusDisplayName = (status: CustomerStatus) => {
    switch (status) {
      case CustomerStatus.ACT:
        return "Active";
      case CustomerStatus.EXP:
        return "Expired";
      case CustomerStatus.MDEL:
        return "Marked for Deletion";
      case CustomerStatus.DEL:
        return "Deleted";
      default:
        return status;
    }
  };

  const getCustomerRowStyle = (customer: Customer) => {
    switch (customer.status) {
      case CustomerStatus.ACT:
        return { color: '#2e7d32' }; // Dark green color for Active customers
      case CustomerStatus.EXP:
        return { color: '#ff9800' }; // Yellow/Orange color for Expired customers
      case CustomerStatus.MDEL:
        return { color: '#d32f2f' }; // Red color for MDEL customers
      case CustomerStatus.DEL:
        return { color: '#9e9e9e' }; // Light grey color for DEL customers
      default:
        return {};
    }
  };

  const columns = [
    {
      name: "id",
      header: {
        render: () => {
          return "Customer Id";
        }
      },
      render: (customer: Customer) => {
        if (!customer) return;
        return (
          <div style={getCustomerRowStyle(customer)}>
            <VuiLink href={`/customer/${customer.customerId}`} onClick={seedCustomerContext(customer)}>
              {customer.customerId}
            </VuiLink>
          </div>
        );
      }
    },
    {
      name: "name",
      header: {
        render: () => {
          return "Name";
        }
      },
      render: (customer: Customer) => {
        if (!customer) return;
        return <div style={getCustomerRowStyle(customer)}>{customer.userName}</div>;
      }
    },
    {
      name: "email",
      header: {
        render: () => {
          return "Email";
        }
      },
      render: (customer: Customer) => {
        if (!customer) return;
        return <div style={getCustomerRowStyle(customer)}>{customer.userEmail}</div>;
      }
    },
    {
      name: "status",
      header: {
        render: () => {
          return "Status";
        }
      },
      render: (customer: Customer) => {
        if (!customer) return;
        return <div style={getCustomerRowStyle(customer)}>{customer.status}</div>;
      }
    }
  ];

  const tableContent = !searchedCustomers?.length ? (
    <VuiText>
      <p>No customers found.</p>
    </VuiText>
  ) : undefined;

  return (
    <div className="pt-5 pl-10">
      <VuiText className="switch-description">
        Search a customer by name, email, or customer id. Click on the customer id to view the customer details.
      </VuiText>
      <VuiSpacer size="l" />
      
      {/* Status Filter Checkboxes */}
      <VuiText>
        <strong>Filter by Customer Status:</strong>
      </VuiText>
      <VuiSpacer size="s" />
      <VuiFlexContainer alignItems="center" spacing="s">
        {Object.values(CustomerStatus).map((status) => (
          <VuiFlexItem key={status} grow={false}>
            <VuiCheckbox
              checked={statusFilters[status]}
              onChange={() => handleStatusFilterChange(status)}
              label={getStatusDisplayName(status)}
            />
          </VuiFlexItem>
        ))}
      </VuiFlexContainer>
      <VuiSpacer size="l" />
      
      <VuiFlexContainer>
        <VuiTextInput
          placeholder="Search customers"
          onSubmit={doSearch}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <VuiButtonSecondary onClick={doSearch} color={"accent"}>
          {isLoading ? <VuiSpinner size="xs" /> : "Search"}
        </VuiButtonSecondary>
      </VuiFlexContainer>
      <VuiSpacer size="l" />
      <VuiTable
        fluid
        idField={(customer: Customer) => customer.customerId.toString()}
        columns={columns}
        rows={searchedCustomers}
        content={tableContent}
      />
    </div>
  );
};
